import React, { useState } from 'react';
import { MapPin, Clock, X, Calendar as CalIcon, CalendarPlus, UserRound } from 'lucide-react';
import { galleryImages, heroImages } from '../mock';
import { ApprovedEventsFeed } from '../components/ApprovedEventsFeed';
import { PublicEventsCalendar } from '../components/PublicEventsCalendar';

const MONTH_MAP = { JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6, JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12 };

function parseTime(str) {
  // "7:30 PM" -> { h: 19, m: 30 }, "2:00 PM – 6:00 PM" -> start = 14:00 end = 18:00
  const m = String(str || '').match(/(\d+):?(\d*)\s*(AM|PM)/gi) || [];
  const parse = (t) => {
    const mm = t.match(/(\d+):?(\d*)\s*(AM|PM)/i);
    if (!mm) return null;
    let h = parseInt(mm[1], 10);
    const min = mm[2] ? parseInt(mm[2], 10) : 0;
    const ampm = mm[3].toUpperCase();
    if (ampm === 'PM' && h < 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    return { h, m: min };
  };
  const start = m[0] ? parse(m[0]) : { h: 12, m: 0 };
  const end = m[1] ? parse(m[1]) : { h: (start.h + 2) % 24, m: start.m };
  return { start, end };
}

function pad(n) { return String(n).padStart(2, '0'); }

function toIcsDate(y, mo, d, h, mi) {
  // Local time — no timezone info (calendar apps interpret in device timezone)
  return `${y}${pad(mo)}${pad(d)}T${pad(h)}${pad(mi)}00`;
}

function buildIcs(ev) {
  const y = Number(ev.year) || new Date().getFullYear();
  const mo = MONTH_MAP[ev.month] || 1;
  const d = parseInt(ev.date, 10);
  const { start, end } = parseTime(ev.time);
  const uid = `${y}-${pad(mo)}-${pad(d)}-${ev.title.replace(/\s+/g, '-').toLowerCase()}@brooklinejppatriots.com`;
  const now = new Date();
  const dtstamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Brookline JP Patriots//Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${toIcsDate(y, mo, d, start.h, start.m)}`,
    `DTEND:${toIcsDate(y, mo, d, end.h, end.m)}`,
    `SUMMARY:${ev.title}`,
    `LOCATION:${ev.location || ''}`,
    `DESCRIPTION:${(ev.desc || '').replace(/\n/g, '\\n')}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ];
  return lines.join('\r\n');
}

function downloadIcs(ev) {
  const ics = buildIcs(ev);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${ev.title.replace(/\s+/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export default function Events() {
  const [selected, setSelected] = useState(null);

  return (
    <div className="font-body">
      {/* Header */}
      <section className="relative pt-40 pb-24 bg-[#0a1f44] overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImages[1]} alt="stadium" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1f44]/70 to-[#0a1f44]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Upcoming</div>
          <h1 className="font-display text-white text-6xl md:text-8xl leading-none">EVENTS</h1>
          <p className="text-white/70 max-w-2xl mt-6 text-lg">
            Games, practices, community events, and the moments that make the Patriots family what we are.
          </p>
        </div>
      </section>

      <div className="stripe-sep" />

      <PublicEventsCalendar onSelect={setSelected} />
      <ApprovedEventsFeed onSelect={setSelected} />

      {/* Gallery */}
      <section className="py-24 bg-[#f5f6f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Memories</div>
            <h2 className="font-display text-[#0a1f44] text-4xl md:text-5xl leading-tight">A PICTORIAL JOURNEY OF OUR LOVE FOR THE SPORT</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {galleryImages.map((src, i) => (
              <div key={i} className="overflow-hidden aspect-[4/3]">
                <img src={src} alt={`memory ${i}`} className="gallery-img w-full h-full object-cover" loading="lazy" decoding="async" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 fade-in" onClick={() => setSelected(null)} data-testid="approved-event-modal">
          <div className="bg-white max-w-2xl w-full relative max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} data-testid="approved-event-modal-close" aria-label="Close event details" className="absolute top-4 right-4 text-white hover:text-[#c8102e] z-10 bg-[#0a1f44]/70 rounded-full p-2">
              <X className="w-5 h-5" />
            </button>
            {selected.img && (
              <img src={selected.img} alt={selected.title} className="w-full h-auto object-contain bg-[#0a1f44]" data-testid="event-modal-flyer" />
            )}
            <div className="bg-[#0a1f44] text-white p-6">
              <div className="flex items-center gap-3 text-[#c8102e]">
                <CalIcon className="w-5 h-5" />
                <div className="font-heading uppercase tracking-widest text-xs" data-testid="approved-event-modal-date">
                  {selected.hasSchedule ? `${selected.day} • ${selected.month} ${selected.date}, ${selected.year}` : 'Program update'}
                </div>
              </div>
              <h3 className="font-display text-3xl mt-3" data-testid="approved-event-modal-title">{selected.title.toUpperCase()}</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3 text-[#0a1f44]" data-testid="approved-event-modal-time">
                <Clock className="w-4 h-4 text-[#c8102e] mt-1" /> <span>{selected.time}</span>
              </div>
              <div className="flex items-start gap-3 text-[#0a1f44]" data-testid="approved-event-modal-location">
                <MapPin className="w-4 h-4 text-[#c8102e] mt-1" /> <span>{selected.location}</span>
              </div>
              {selected.desc && <p className="text-[#0a1f44]/75 leading-relaxed" data-testid="approved-event-modal-description">{selected.desc}</p>}
              <div className="inline-flex items-center gap-1.5 text-[11px] text-[#0a1f44]/55" data-testid="approved-event-modal-author">
                <UserRound className="w-3.5 h-3.5 text-[#c8102e]" /> Submitted by {selected.contributorName}
              </div>
              {selected.hasSchedule && (
                <button onClick={() => downloadIcs(selected)} data-testid="event-add-to-calendar" className="w-full mt-4 bg-white border-2 border-[#0a1f44] hover:bg-[#0a1f44] hover:text-white text-[#0a1f44] font-heading uppercase tracking-widest text-xs py-3 inline-flex items-center justify-center gap-2 transition-colors">
                  <CalendarPlus className="w-4 h-4" /> Add to Calendar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
